import { useCallback, useEffect, useState } from 'react';
import type { CoreOrganization } from '@/lib/zifekCore';
import {
  fetchObservabilityStats,
  listMetrics,
  listTraces,
  METRIC_CATEGORIES,
  OBSERVABILITY_SIGNALS,
  recordMetric,
  recordTrace,
  severityMeta,
  type CoreMetric,
  type CoreTrace,
} from '@/lib/coreObservability';

interface Props {
  commerceId: number;
  org: CoreOrganization;
}

const INPUT = 'w-full px-3 py-2 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-colors';

export default function ObservabilityTab({ org }: Props) {
  const [metrics, setMetrics] = useState<CoreMetric[]>([]);
  const [traces, setTraces] = useState<CoreTrace[]>([]);
  const [stats, setStats] = useState({ metrics: 0, warnings: 0, criticals: 0, avgLatency: 0, traces: 0, errorTraces: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [form, setForm] = useState({ metricKey: '', label: '', category: 'Latence', value: 0, unit: 'ms' });
  const [traceForm, setTraceForm] = useState({ name: '', durationMs: 120 });

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [m, t, s] = await Promise.all([listMetrics(org.id), listTraces(org.id), fetchObservabilityStats(org.id)]);
      setMetrics(m);
      setTraces(t);
      setStats(s);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, [org.id]);

  useEffect(() => { load(); }, [load]);

  const addMetric = async () => {
    if (!form.metricKey.trim()) { setError('La clé de métrique est requise.'); return; }
    setBusy('metric');
    setError('');
    try {
      await recordMetric({
        organizationId: org.id, tenantId: org.tenant_id, metricKey: form.metricKey.trim(),
        label: form.label || form.metricKey, category: form.category, value: form.value, unit: form.unit,
        severity: form.value >= 1000 ? 'warning' : 'info',
      });
      setForm({ metricKey: '', label: '', category: 'Latence', value: 0, unit: 'ms' });
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); } finally { setBusy(''); }
  };

  const addTrace = async () => {
    if (!traceForm.name.trim()) { setError('Le nom de la trace est requis.'); return; }
    setBusy('trace');
    setError('');
    try {
      await recordTrace({
        organizationId: org.id, tenantId: org.tenant_id, name: traceForm.name.trim(),
        durationMs: traceForm.durationMs, status: traceForm.durationMs > 1000 ? 'slow' : 'ok',
        spans: [{ step: 'db', duration_ms: Math.round(traceForm.durationMs * 0.4) }, { step: 'render', duration_ms: Math.round(traceForm.durationMs * 0.2) }],
      });
      setTraceForm({ name: '', durationMs: 120 });
      await load();
    } catch (err) { setError(err instanceof Error ? err.message : 'Erreur.'); } finally { setBusy(''); }
  };

  const byCategory = METRIC_CATEGORIES.map((c) => ({ ...c, items: metrics.filter((m) => m.category === c.key) })).filter((c) => c.items.length > 0);

  return (
    <div className="space-y-5">
      <p className="text-xs text-foreground-500 max-w-3xl">
        <span className="font-semibold text-foreground-700">Observabilité</span> : logs, métriques, erreurs, traces,
        temps de réponse, usage API et AI Tools. Objectif : identifier vite les erreurs, lenteurs, conflits,
        appels excessifs et toute fuite d’isolation entre entreprises.
      </p>

      {error && <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-700">{error}</div>}

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-line-chart-line text-lg text-primary-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.metrics}</p>
          <p className="text-xs text-foreground-500">Métriques</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-timer-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.avgLatency} ms</p>
          <p className="text-xs text-foreground-500">Latence moyenne</p>
        </div>
        <div className="rounded-lg border bg-background-50 border-background-200/70 p-4">
          <i className="ri-error-warning-line text-lg text-accent-600"></i>
          <p className="text-lg font-bold text-foreground-900 mt-1">{stats.warnings} <span className="text-sm text-foreground-500">/ {stats.criticals} crit.</span></p>
          <p className="text-xs text-foreground-500">Alertes</p>
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-radar-line text-primary-600"></i>Signaux à surveiller</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {OBSERVABILITY_SIGNALS.map((s) => (
            <div key={s.key} className="rounded-lg border border-background-200/70 bg-background-100/60 p-3">
              <p className="text-xs font-medium text-foreground-800 flex items-center gap-2"><i className={`${s.icon} text-primary-600`}></i>{s.label}</p>
              <p className="text-[11px] text-foreground-500 mt-1">{s.hint}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b border-background-200/60 flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center"><i className="ri-line-chart-line"></i></span>
          <h4 className="text-sm font-semibold text-foreground-900">Métriques par catégorie</h4>
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i></div>
        ) : byCategory.length === 0 ? (
          <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune métrique enregistrée.</p>
        ) : (
          <div className="divide-y divide-background-200/60">
            {byCategory.map((cat) => (
              <div key={cat.key} className="px-4 py-3">
                <p className="text-xs font-semibold text-foreground-700 flex items-center gap-2 mb-2"><i className={cat.icon}></i>{cat.label}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {cat.items.map((m) => {
                    const sev = severityMeta(m.severity);
                    return (
                      <div key={m.id} className="rounded-lg border border-background-200/70 p-3 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-xs text-foreground-700 truncate">{m.label || m.metric_key}</p>
                          <p className={`text-[10px] inline-flex px-2 py-0.5 rounded-full ${sev.className}`}>{sev.label}</p>
                        </div>
                        <p className="text-sm font-bold text-foreground-900 whitespace-nowrap">{m.value} <span className="text-[10px] font-normal text-foreground-500">{m.unit}</span></p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-background-200/60 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-foreground-900 flex items-center gap-2"><i className="ri-git-commit-line text-accent-600"></i>Traces récentes</h4>
            <span className="text-[11px] text-foreground-400">{stats.traces} traces · {stats.errorTraces} erreurs</span>
          </div>
          {traces.length === 0 ? (
            <p className="px-4 py-6 text-xs text-foreground-400 text-center">Aucune trace.</p>
          ) : (
            <div className="divide-y divide-background-200/60 max-h-72 overflow-y-auto">
              {traces.slice(0, 30).map((t) => (
                <div key={t.id} className="px-4 py-2.5 flex items-center gap-3">
                  <i className={t.status === 'error' ? 'ri-close-circle-line text-red-600' : t.status === 'slow' ? 'ri-timer-line text-accent-600' : 'ri-checkbox-circle-line text-secondary-600'}></i>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-foreground-700 truncate">{t.name}</p>
                    <p className="text-[10px] text-foreground-400">{t.span_count || 0} spans · {t.started_at ? new Date(t.started_at).toLocaleString('fr-FR') : ''}</p>
                  </div>
                  <span className={`text-xs font-semibold whitespace-nowrap ${(t.duration_ms || 0) > 1000 ? 'text-accent-600' : 'text-foreground-700'}`}>{t.duration_ms} ms</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-background-50 border border-background-200/70 rounded-lg p-4 space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-add-line text-primary-600"></i>Enregistrer une métrique</h4>
            <div className="space-y-2">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input className={INPUT} placeholder="Clé (ex : api_error_rate)" value={form.metricKey} onChange={(e) => setForm({ ...form, metricKey: e.target.value })} />
                <input className={INPUT} placeholder="Libellé" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <select className={INPUT} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {METRIC_CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                <input type="number" className={INPUT} placeholder="Valeur" value={form.value} onChange={(e) => setForm({ ...form, value: Number(e.target.value) })} />
                <input className={INPUT} placeholder="Unité" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
              </div>
              <button onClick={addMetric} disabled={busy === 'metric'} className="px-4 py-2 rounded-full bg-primary-500 text-background-50 text-xs font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2">
                {busy === 'metric' ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-line"></i>} Ajouter
              </button>
            </div>
          </div>
          <div className="border-t border-background-200/60 pt-3">
            <h4 className="text-sm font-semibold text-foreground-900 mb-2 flex items-center gap-2"><i className="ri-git-commit-line text-accent-600"></i>Simuler une trace</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <input className={`${INPUT} sm:col-span-2`} placeholder="Nom (ex : GET /customers)" value={traceForm.name} onChange={(e) => setTraceForm({ ...traceForm, name: e.target.value })} />
              <input type="number" className={INPUT} placeholder="Durée (ms)" value={traceForm.durationMs} onChange={(e) => setTraceForm({ ...traceForm, durationMs: Number(e.target.value) })} />
            </div>
            <button onClick={addTrace} disabled={busy === 'trace'} className="mt-2 px-4 py-2 rounded-full bg-background-100 text-foreground-700 text-xs font-semibold whitespace-nowrap cursor-pointer disabled:opacity-50 flex items-center gap-2">
              {busy === 'trace' ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-add-line"></i>} Enregistrer la trace
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}